import { store } from "../redux/store";
import { getVapidPublicKey, subscribeWebPush } from "../redux/actions/notifications";
import { ADD_PUSH_NOTIFICATION } from "../redux/constants/actionTypes";

/**
 * Convert a URL-safe base64 string to a Uint8Array (for applicationServerKey)
 */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Register the push service worker
 */
export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) {
    console.warn("[PUSH] - Service workers are not supported in this browser");
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register("/sw-push.js");
    console.log("[PUSH] - Push SW registered with scope:", registration.scope);
    return registration;
  } catch (error) {
    console.error("[PUSH] - Push SW registration failed:", error);
    return null;
  }
}

/**
 * Request notification permission from the user
 */
export async function requestNotificationPermission() {
  if (!("Notification" in window)) {
    console.warn("[PUSH] - Notifications are not supported in this browser");
    return "denied";
  }

  if (Notification.permission === "granted") {
    return "granted";
  }

  if (Notification.permission === "denied") {
    // Permission was explicitly blocked by the user – return silently.
    return "denied";
  }

  const permission = await Notification.requestPermission();
  console.warn("[PUSH] - Notifications Permission requestNotfication", permission);
  return permission;
}

/**
 * Subscribe the browser to web push notifications.
 * 1. Fetches VAPID public key from the server
 * 2. Creates a push subscription via the Push API
 * 3. Sends the subscription details to the backend
 */
export async function subscribeToPushNotifications() {
  try {
    // 1. Get permission
    const permission = await requestNotificationPermission();
    if (permission !== "granted") {
      console.warn("[PUSH] - Notification permission not granted");
      return null;
    }

    // 2. Register service worker and wait until it is active
    const registered = await registerServiceWorker();
    if (!registered) return null;

    // navigator.serviceWorker.ready resolves only once an active SW controls the page,
    // which is required before pushManager.subscribe() can succeed.
    const registration = await navigator.serviceWorker.ready;

    console.log("[PUSH] - SW Registration successful ")

    // 3. Check for existing subscription
    const existingSubscription = await registration.pushManager.getSubscription();
    if (existingSubscription) {
      console.log("[PUSH] - Already subscribed to push notifications");
      return existingSubscription;
    }

    // 4. Get VAPID public key from server
    const vapidResult = await store.dispatch(getVapidPublicKey());
    const vapidPublicKey = vapidResult?.public_key || vapidResult;
    if (!vapidPublicKey) {
      console.error("[PUSH] - Could not retrieve VAPID public key");
      return null;
    }

    // 5. Subscribe to push
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });

    // 6. Extract subscription keys
    const subscriptionJSON = subscription.toJSON();
    const userData = JSON.parse(localStorage.getItem("userData"));
    const profileId = userData?.profile_id || store.getState()?.profile?.user?.profile_id;

    console.log("PUSH Subscription JSON", {subscription, subscriptionJSON})

    // 7. Send subscription to backend
    await store.dispatch(
      subscribeWebPush({
        profile: profileId,
        endpoint: subscriptionJSON.endpoint,
        p256dh: subscriptionJSON.keys.p256dh,
        auth: subscriptionJSON.keys.auth,
        user_agent: navigator.userAgent,
      })
    );

    console.log("[PUSH] - Successfully subscribed to push notifications");
    return subscription;
  } catch (error) {
    console.error("[PUSH] - Failed to subscribe to push notifications:", error);
    return null;
  }
}

/**
 * Unsubscribe from push notifications
 */
export async function unsubscribeFromPushNotifications() {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      await subscription.unsubscribe();
      console.log("[PUSH] - Unsubscribed from push notifications");
      return true;
    }
    return false;
  } catch (error) {
    console.error("[PUSH] - Failed to unsubscribe:", error);
    return false;
  }
}

/**
 * Check if the user is currently subscribed to push notifications
 */
export async function isPushSubscribed() {
  try {
    if (!("serviceWorker" in navigator)) return false;
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch {
    return false;
  }
}

/**
 * Listen for messages from the service worker (e.g., subscription changes)
 */
export function listenForServiceWorkerMessages() {
  if (!("serviceWorker" in navigator)) return;

  navigator.serviceWorker.addEventListener("message", (event) => {
    if (event.data?.type === "PUSH_SUBSCRIPTION_CHANGE") {
      // Re-send new subscription to backend
      const subscription = event.data.subscription;
      const userData = JSON.parse(localStorage.getItem("userData"));
      const profileId = userData?.profile_id || store.getState()?.profile?.user?.profile_id;

      store.dispatch(
        subscribeWebPush({
          profile: profileId,
          endpoint: subscription.endpoint,
          p256dh: subscription.keys.p256dh,
          auth: subscription.keys.auth,
          user_agent: navigator.userAgent,
        })
      );
    }

    if (event.data?.type === "PUSH_NOTIFICATION_RECEIVED") {
      store.dispatch({
        type: ADD_PUSH_NOTIFICATION,
        payload: event.data.notification,
      });
    }
  });
}
